import React, { useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getClerkToken } from '../../auth/clerk';

const SOP_EMBED_SCRIPT_ID = 'sop-assistant-embed-script';

let embedScriptPromise;

function loadEmbedScript(scriptUrl) {
  if (window.SopAssistant) {
    return Promise.resolve();
  }

  if (!embedScriptPromise) {
    embedScriptPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById(SOP_EMBED_SCRIPT_ID);

      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = SOP_EMBED_SCRIPT_ID;
      script.async = true;
      script.src = scriptUrl;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    }).catch((error) => {
      embedScriptPromise = null;
      throw error;
    });
  }

  return embedScriptPromise;
}

export default function SopAssistantTrigger() {
  const flags = useFlags();
  const containerRef = useRef(null);
  const assistantRef = useRef(null);

  const isAvailable = flags['id360admin-sop-assistant'] === true;

  useEffect(() => {
    const embedScript = process.env.REACT_APP_SOP_EMBED_SCRIPT;
    const assistantOrigin = process.env.REACT_APP_SOP_ASSISTANT_ORIGIN;

    if (!isAvailable || !embedScript || !assistantOrigin || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;

    loadEmbedScript(embedScript)
      .then(() => {
        if (cancelled || !containerRef.current || !window.SopAssistant) {
          return;
        }

        assistantRef.current = window.SopAssistant.mount({
          assistantOrigin,
          container: containerRef.current,
          getToken: getClerkToken
        });
      })
      .catch(() => {
        // The host remains usable if the optional assistant asset is unavailable.
      });

    return () => {
      cancelled = true;
      assistantRef.current && assistantRef.current.destroy();
      assistantRef.current = null;
    };
  }, [isAvailable]);

  if (!isAvailable) {
    return null;
  }

  return (
    <div className="sop-assistant-launcher">
      <button
        className="sop-assistant-launcher__trigger"
        type="button"
        onClick={() => assistantRef.current ? assistantRef.current.open() : null}
      >
        SOP Assistant
      </button>
      <div className="sop-assistant-launcher__frame" ref={containerRef} />
    </div>
  );
}
